import type {
	EventGrantDisplayInput,
	EventGrantDisplayState,
} from "../types/eventGrant.type";

/**
 * Map a member's ROW-level server state to a single display state (AC3).
 *
 * Every branch reads from row-level API data — THIS member's grant status,
 * COI verdict, and whether they are an event-grant principal — plus the
 * evaluation-closed context. It never reads the viewer's capability: the
 * resolver's `read_only` is per-caller, so using it here would badge every
 * member with the *viewer's* state (review F1). The FE never infers liveness
 * (C8/BE-6); it only presents the server's state. AC3's distinction — a
 * post-close AUTO-RELEASE vs a not-yet-accepted invite vs an active member —
 * is derived deterministically from 007's close rule.
 */
export const deriveEventGrantDisplayState = ({
	status,
	coi,
	isEventGrant = false,
	evaluationClosed = false,
}: EventGrantDisplayInput): EventGrantDisplayState => {
	// Terminal denials win outright.
	if (status === "revoked") {
		return "revoked";
	}
	if (status === "declined") {
		return "declined";
	}

	// COI verdicts override the live/active presentation.
	if (coi) {
		if (coi.status === "suspended") {
			return "suspended";
		}
		if (coi.status === "blocked" || coi.status === "flagged") {
			return "flagged";
		}
	}

	// Not yet accepted — invited ≠ live (C8).
	if (status === "pending" || status === "invited") {
		return "pending";
	}

	// Accepted/live: an event-grant principal is auto-released once the
	// evaluation closes (007, deterministic, per-grant). Own-tenant internal
	// members are never grant-released and stay "accepted".
	if (status === "accepted" && isEventGrant && evaluationClosed) {
		return "released";
	}
	return "accepted";
};
